import {
	DropdownMenuRadioItem,
	DropdownMenuItemIndicator,
} from '@radix-ui/react-dropdown-menu'
import { Form, useLoaderData, useSearchParams } from '@remix-run/react'
import { motion } from 'framer-motion'
import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuRadioGroup,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu'
import useMediaQuery from '#app/hooks/useMediaQuery'
import { type loader } from '#app/routes/_public+/products+/$slug'
import { cn } from '#app/utils/misc'
import { SortButton } from './sort-button'

const SortingDropdown = ({
	selectedSortOrder,
	...props
}: {
	selectedSortOrder?: string
}) => {
	const [searchParams, setParams] = useSearchParams()
	const [isSelectorOpen, setIsSelectorOpen] = useState(false)
	const sortButtonRef = useRef<HTMLButtonElement>(null)
	const [dropdownWidth, setDropdownWidth] = useState<string | number>('auto')
	const isDesktop = useMediaQuery({ mobile: '820px' })

	const { onSaleAvailable } = useLoaderData<typeof loader>()
	const initialSortOrder = searchParams.get('sortingOrder') || 'new'

	const [selectedSort, setSelectedSort] = useState(initialSortOrder)

	let { t } = useTranslation('search')

	useEffect(() => {
		const sortOrder = searchParams.get('sortingOrder') || 'new'
		setSelectedSort(sortOrder)
	}, [searchParams])

	useEffect(() => {
		if (sortButtonRef.current) {
			setDropdownWidth(sortButtonRef.current.offsetWidth)
		}
	}, [isSelectorOpen])

	const getSortText = () => {
		switch (selectedSort) {
			case 'popular':
				return t('sort_by') + ': ' + t('product_list_page:most_popular')
			case 'new':
				return t('sort_by') + ': ' + t('newest_tag')
			case 'price_asc':
				return t('sort_by') + ': ' + t('lowest_price')
			case 'price_desc':
				return t('sort_by') + ': ' + t('highest_price')
			case 'on_sale':
				return t('sort_by') + ': ' + t('product_list_page:on_sale')
			default:
				return t('sort_by')
		}
	}

	const handleOnChange = (value: string) => {
		const newSearchParams = new URLSearchParams(searchParams)

		newSearchParams.set('sortingOrder', value)
		newSearchParams.delete('p')

		setParams(newSearchParams)
		setSelectedSort(value)
	}
	return (
		<DropdownMenu onOpenChange={setIsSelectorOpen} {...props}>
			<DropdownMenuTrigger asChild>
				<SortButton
					ref={sortButtonRef}
					isSelectorOpen={isSelectorOpen}
					sortText={getSortText()}
				/>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				align="end"
				className="min-w-full"
				style={!isDesktop ? { width: dropdownWidth } : {}}
			>
				<motion.div
					initial={{ opacity: 0, maxHeight: 0 }}
					animate={{ opacity: 1, maxHeight: '240px' }}
					exit={{ opacity: 0, maxHeight: 0 }}
					transition={{ duration: 0.3, ease: 'linear' }}
				>
					<Form method="get">
						<DropdownMenuRadioGroup
							className="justify-between"
							value={selectedSort}
							onValueChange={value => {
								handleOnChange(value as string)
							}}
						>
							<DropdownMenuRadioItem
								value="popular"
								className={cn(
									'flex cursor-pointer items-center rounded-sm px-4 py-2 outline-none',
									selectedSort === 'popular'
										? 'border-green-500'
										: 'border-gray-300',
									'hover:bg-beige-70',
								)}
							>
								<span
									className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${
										selectedSort === 'popular'
											? 'border-green-500'
											: 'border-gray-300'
									}`}
								>
									<DropdownMenuItemIndicator>
										<span
											className={`flex h-3 w-3 rounded-full ${
												selectedSort === 'popular' ? 'bg-black' : ''
											}`}
										/>
									</DropdownMenuItemIndicator>
								</span>
								<span className="ml-2">
									{t('product_list_page:most_popular')}
								</span>
							</DropdownMenuRadioItem>
							<DropdownMenuRadioItem
								value="new"
								className={cn(
									'flex cursor-pointer items-center rounded-sm px-4 py-2 outline-none',
									selectedSort === 'new'
										? 'border-green-500'
										: 'border-gray-300',
									'hover:bg-beige-70',
								)}
							>
								<span
									className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${
										selectedSort === 'new'
											? 'border-green-500'
											: 'border-gray-300'
									}`}
								>
									<DropdownMenuItemIndicator>
										<span
											className={`flex h-3 w-3 rounded-full ${
												selectedSort === 'new' ? 'bg-black' : ''
											}`}
										/>
									</DropdownMenuItemIndicator>
								</span>
								<span className="ml-2">{t('newest_tag')}</span>
							</DropdownMenuRadioItem>
							<DropdownMenuRadioItem
								value="price_asc"
								className={cn(
									'flex cursor-pointer items-center rounded-sm px-4 py-2 outline-none',
									selectedSort === 'price_asc'
										? 'border-green-500'
										: 'border-gray-300',
									'hover:bg-beige-70',
								)}
							>
								<span
									className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${
										selectedSort === 'price_asc'
											? 'border-green-500'
											: 'border-gray-300'
									}`}
								>
									<DropdownMenuItemIndicator>
										<span
											className={`flex h-3 w-3 rounded-full ${
												selectedSort === 'price_asc' ? 'bg-black' : ''
											}`}
										/>
									</DropdownMenuItemIndicator>
								</span>
								<span className="ml-2">{t('lowest_price')}</span>
							</DropdownMenuRadioItem>
							<DropdownMenuRadioItem
								value="price_desc"
								className={cn(
									'flex cursor-pointer items-center rounded-sm px-4 py-2 outline-none',
									selectedSort === 'price_desc'
										? 'border-green-500'
										: 'border-gray-300',
									'hover:bg-beige-70',
								)}
							>
								<span
									className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${
										selectedSort === 'price_desc'
											? 'border-green-500'
											: 'border-gray-300'
									}`}
								>
									<DropdownMenuItemIndicator>
										<span
											className={`flex h-3 w-3 rounded-full ${
												selectedSort === 'price_desc' ? 'bg-black' : ''
											}`}
										/>
									</DropdownMenuItemIndicator>
								</span>
								<span className="ml-2">{t('highest_price')}</span>
							</DropdownMenuRadioItem>
							{onSaleAvailable.count > 0 && (
								<DropdownMenuRadioItem
									value="on_sale"
									className={cn(
										'flex cursor-pointer items-center rounded-sm px-4 py-2 outline-none',
										selectedSort === 'on_sale'
											? 'border-green-500'
											: 'border-gray-300',
										'hover:bg-beige-70',
									)}
								>
									<span
										className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${
											selectedSort === 'on_sale'
												? 'border-green-500'
												: 'border-gray-300'
										}`}
									>
										<DropdownMenuItemIndicator>
											<span
												className={`flex h-3 w-3 rounded-full ${
													selectedSort === 'on_sale' ? 'bg-black' : ''
												}`}
											/>
										</DropdownMenuItemIndicator>
									</span>
									<span className="ml-2">{t('product_list_page:on_sale')}</span>
								</DropdownMenuRadioItem>
							)}
						</DropdownMenuRadioGroup>
					</Form>
				</motion.div>
			</DropdownMenuContent>
		</DropdownMenu>
	)
}

export { SortingDropdown }
